import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChatNotification } from '../../contexts/ChatNotificationContext';
import { useChamadosStats } from '../../hooks/useChamados';
import { isCrmUser } from '../CrmRoute/CrmRoute';
import styles from './Sidebar.module.css';
import tatuLogo from '../../assets/tatuchat-logo.png';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [lojaFisicaExpanded, setLojaFisicaExpanded] = useState(false);
  const [limeiraExpanded, setLimeiraExpanded] = useState(false);
  const [ecommerceExpanded, setEcommerceExpanded] = useState(false);
  const [clientesExpanded, setClientesExpanded] = useState(false);
  const [faturamentoV2Expanded, setFaturamentoV2Expanded] = useState(false);
  const [crmExpanded, setCrmExpanded] = useState(false);
  const location = useLocation();
  const { user, isAdmin, isFornecedor, logout, isSuperAdmin, isRootAdmin, hasPermission } = useAuth();
  const canSeeCrm = isCrmUser(user);
  const { totalNaoLidas } = useChatNotification();
  // Badge de chamados abertos (só admin) — staleTime 2min, não bloqueia render
  const { data: chamadosStats } = useChamadosStats();
  // Backend retorna { stats: { por_status: [{status, total}, ...] } }
  const chamadosAbertos = isAdmin()
    ? (chamadosStats?.stats?.por_status?.find(s => s.status === 'aberto')?.total || 0)
    : 0;

  // Paths permitidos para fornecedores
  const fornecedorAllowedPaths = ['/limeira', '/quadro-branco'];

  const toggleLojaFisica = () => {
    setLojaFisicaExpanded(!lojaFisicaExpanded);
  };

  const toggleLimeira = () => {
    setLimeiraExpanded(!limeiraExpanded);
  };

  const toggleEcommerce = () => {
    setEcommerceExpanded(!ecommerceExpanded);
  };

  const toggleClientes = () => {
    setClientesExpanded(!clientesExpanded);
  };

  const toggleFaturamentoV2 = () => {
    setFaturamentoV2Expanded(!faturamentoV2Expanded);
  };

  const toggleCrm = () => {
    setCrmExpanded(!crmExpanded);
  };

  const isActive = (path) => {
    if (path === '/ecommerce') {
      return location.pathname.startsWith('/ecommerce');
    }
    if (path === '/loja-fisica') {
      return location.pathname.startsWith('/loja-fisica');
    }
    if (path === '/limeira') {
      return location.pathname.startsWith('/limeira');
    }
    if (path === '/clientes') {
      return location.pathname.startsWith('/clientes');
    }
    if (path === '/crm') {
      return location.pathname.startsWith('/crm');
    }
    return location.pathname === path;
  };

  const menuItems = [
    {
      path: '/',
      label: 'Home',
    },
    {
      path: '/pdv',
      label: 'PDV',
    },
    {
      path: '/assistente-ia',
      label: 'Assistente IA',
    },
    {
      path: '/catalogo-fornecedor',
      label: 'Catálogos Fornecedores',
    },
    // Catálogo da Loja Centro (link do cliente): produtos, pedidos, clientes, links, relatório e histórico.
    ...((isAdmin() || hasPermission('admin_catalogo')) ? [{
      path: '/catalogo-cliente',
      label: 'Catálogo Cliente',
    }] : []),
    {
      path: '/roadmap',
      label: 'Roadmap',
      rootOnly: true,
    },
    {
      path: '/quadro-branco',
      label: 'Quadro Branco',
    },
    {
      path: '/chat',
      label: 'Chat da Equipe',
      badge: totalNaoLidas > 0 ? totalNaoLidas : null,
    },
    {
      path: '/octadesk',
      label: 'Octadesk',
    },
    // Financeiro: requer permissão 'financeiro' (gerenciada via UI de usuários)
    ...(hasPermission('financeiro') ? [{
      path: '/financeiro',
      label: 'Financeiro',
    }] : []),
    {
      path: '/notifications-history',
      label: 'Minhas Notificações',
    },
    {
      path: '/chamados',
      label: 'Chamados',
      badge: chamadosAbertos > 0 ? chamadosAbertos : null,
    },
    {
      path: '/crm',
      label: 'Atendimento CRM',
      crmUserOnly: true,
      hasSubmenu: true,
      submenu: [
        { path: '/crm',           label: 'Inbox' },
        { path: '/crm/canais',    label: 'Canais' },
        { path: '/crm/supervisao',label: 'Supervisao' },
      ]
    },
    {
      path: '/ecommerce',
      label: 'E-commerce',
      hasSubmenu: true,
      submenu: [
        {
          path: '/ecommerce',
          label: 'Catálogo de Produtos'
        },
        {
          path: '/ecommerce/aprovacao-compras',
          label: '🔍 Aprovações de Compras'
        },
        {
          path: '/ecommerce/historico-compras',
          label: '📋 Histórico de Compras'
        }
      ]
    },
    {
      path: '/macro',
      label: 'Macro',
    },
    {
      path: '/loja-fisica',
      label: 'Loja Física',
      hasSubmenu: true,
      submenu: [
        {
          path: '/loja-fisica/centro',
          label: 'Loja Centro'
        },
        {
          path: '/loja-fisica/caxias',
          label: 'Loja Caxias'
        },
        {
          path: '/loja-fisica/aprovacao-compras',
          label: '🔍 Aprovações de Compras'
        },
        {
          path: '/loja-fisica/historico-compras',
          label: '📋 Histórico de Compras'
        }
      ]
    },
    {
      path: '/ecommerce/conferencia',
      label: 'Sistema de Conferência',
    },
    {
      path: '/picos-queda',
      label: 'Picos de Vendas',
    },
    {
      path: '/mapa-de-calor',
      label: 'Analytics',
    },
    {
      path: '/pedidos-materiais',
      label: 'Pedidos de Materiais',
    },
    {
      path: '/clientes',
      label: 'Clientes',
      hasSubmenu: true,
      submenu: [
        {
          path: '/clientes-360',
          label: 'Clientes 360 (Novo)'
        },
        {
          path: '/clientes',
          label: 'Gestão de Clientes'
        },
        {
          path: '/clientes-inativos',
          label: 'Recuperação de Clientes'
        },
        {
          path: '/controle-contato-clientes',
          label: 'Controle Contato Clientes'
        }
      ]
    },
    {
      path: '/produtos-consolidados',
      label: 'Consolidado 3 Lojas',
    },

    {
      path: '/relatorios-faturamento',
      label: 'Relatórios Faturamento',
    },
    {
      path: '/relatorios-faturamento-v2',
      label: 'Faturamento V2',
      hasSubmenu: true,
      submenu: [
        { path: '/relatorios-faturamento-v2', label: '📊 Consolidado' },
        { path: '/relatorios-faturamento-v2/ecommerce', label: '🛒 E-commerce' },
        { path: '/relatorios-faturamento-v2/centro', label: '🏬 Loja Centro' },
        { path: '/relatorios-faturamento-v2/caxias', label: '🏬 Loja Caxias' },
      ]
    },
    {

      path: '/limeira',
      label: 'Limeira',
      hasSubmenu: true,
      submenu: [
        {
          path: '/limeira/produtos',
          label: 'Catálogo de Produtos'
        },
        {
          path: '/limeira',
          label: 'Solicitações'
        },
        {
          path: '/limeira/fornecedor',
          label: 'Pedidos Aprovados'
        },
        {
          path: '/limeira/conferencia',
          label: 'Conferência'
        },
        {
          path: '/limeira/historico',
          label: 'Histórico'
        }
      ]
    }
  ];
  const adminMenuItems = [
    {
      path: '/catalogo-admin',
      label: 'Admin Catálogo',
    },
    {
      path: '/usuarios',
      label: 'Usuários',
    },
    {
      path: '/admin/logs',
      label: 'Logs do Sistema',
    },
    {
      path: '/assistente-ia/analytics',
      label: 'IA Analytics',
      superAdminOnly: true,
    },
    {
      path: '/admin/ops',
      label: 'Gringão Ops v2',
    },
    {
      path: '/admin/chat-historico',
      label: 'Histórico do Chat',
      superAdminOnly: true,
    }
  ];

  return (
    <>
      <button
        className={`${styles.toggleButton} ${isOpen ? styles.toggleButtonOpen : ''}`}
        onClick={toggleSidebar}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {isOpen && <div className={styles.overlay} onClick={toggleSidebar} />}

      <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <div className={styles.logoIconWrapper} aria-hidden="true">
              <img src={tatuLogo} alt="" className={styles.logoImg} />
            </div>
            <div className={styles.logoContent}>
              <h1 className={styles.logoTitle}>GRINGÃO</h1>
              <span className={styles.logoSubtitle}>Dashboard</span>
            </div>
          </div>
          <button className={styles.sidebarToggleHeader} onClick={toggleSidebar} title={isOpen ? 'Recolher menu' : 'Expandir menu'}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
            </svg>
          </button>
        </div>

        <nav className={styles.nav}>
          <div className={styles.navSection}>
            <h2 className={styles.navTitle}>Menu Principal</h2>
            {menuItems
              .filter(item => {
                // Roadmap: exclusivo do admin ROOT (thiago@gmail.com)
                if (item.rootOnly && !isRootAdmin()) {
                  return false;
                }
                // Ocultar Home para usuários não-admin
                if (item.path === '/' && !isAdmin()) {
                  return false;
                }
                // CRM Atendimento: acesso restrito a usuários autorizados
                if (item.crmUserOnly && !canSeeCrm) {
                  return false;
                }
                // Se for fornecedor, mostrar apenas paths permitidos (Limeira e Quadro Branco)
                if (isFornecedor()) {
                  return fornecedorAllowedPaths.some(allowedPath => item.path.startsWith(allowedPath));
                }
                return true;
              })
              .map((item) => {
                // Determinar qual toggle e estado usar baseado no path
                const toggleFunction = item.path === '/ecommerce' ? toggleEcommerce :
                  item.path === '/loja-fisica' ? toggleLojaFisica :
                    item.path === '/limeira' ? toggleLimeira :
                      item.path === '/clientes' ? toggleClientes :
                        item.path === '/relatorios-faturamento-v2' ? toggleFaturamentoV2 :
                          item.path === '/crm' ? toggleCrm :
                            null;
                const isExpanded = item.path === '/ecommerce' ? ecommerceExpanded :
                  item.path === '/loja-fisica' ? lojaFisicaExpanded :
                    item.path === '/limeira' ? limeiraExpanded :
                      item.path === '/clientes' ? clientesExpanded :
                        item.path === '/relatorios-faturamento-v2' ? faturamentoV2Expanded :
                          item.path === '/crm' ? crmExpanded :
                            false;

                return (
                  <div key={item.path}>
                    {item.hasSubmenu ? (
                      <>
                        <div
                          className={`${styles.navItem} ${isActive(item.path) ? styles.active : ''}`}
                          onClick={toggleFunction}
                          title={item.label}
                        >
                          <span className={styles.navInicial} aria-hidden="true">{item.label.trim().charAt(0)}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                          <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </div>
                        {isExpanded && (
                          <div className={styles.submenu}>
                            {item.submenu
                              .filter(subItem => !subItem.adminOnly || isAdmin())
                              .map((subItem) => (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  className={`${styles.submenuItem} ${location.pathname === subItem.path ? styles.active : ''}`}
                                >
                                  <span className={styles.submenuLabel}>{subItem.label}</span>
                                </Link>
                              ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        className={`${styles.navItem} ${isActive(item.path) ? styles.active : ''}`}
                        title={item.label}
                      >
                        <span className={styles.navInicial} aria-hidden="true">{item.label.trim().charAt(0)}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                        {item.badge && (
                          <span className={styles.chatBadge}>{item.badge > 99 ? '99+' : item.badge}</span>
                        )}
                      </Link>
                    )}
                  </div>
                );
              })}
          </div>

          {isAdmin && (
            <div className={styles.navSection}>
              <h2 className={styles.navTitle}>Administração</h2>
              {adminMenuItems
                .filter(item => !item.superAdminOnly || isSuperAdmin())
                .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${styles.navItem} ${isActive(item.path) ? styles.active : ''}`}
                  title={item.label}
                >
                  <span className={styles.navInicial} aria-hidden="true">{item.label.trim().charAt(0)}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                </Link>
              ))}
            </div>
          )}

          {!isFornecedor() && (
            <div className={styles.navSection}>
              <h2 className={styles.navTitle}>Informações</h2>
              <div className={styles.infoCard}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Total Produtos:</span>
                  <span className={styles.infoValue}>2.347+</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Bancos Ativos:</span>
                  <span className={styles.infoValue}>2</span>
                </div>
              </div>
            </div>
          )}
        </nav>

        <div className={styles.footer}>
          <div className={styles.createdBy}>
            <span>created by thmoraes</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
